<template>
  <div>
    <page-header
      :title="'Grade Assignment Submission'"
      :container-class="containerClass"
      :show-back="true"
      class="mb-32pt"
    />

    <div class="page-section">
      <div :class="containerClass">
        <!-- Loading State -->
        <div v-if="isLoading" class="text-center py-5">
          <b-spinner class="align-middle mr-2"></b-spinner>
          <strong>Loading submission...</strong>
        </div>

        <!-- Submission Details -->
        <div v-else-if="submission">
          <!-- AI Error Banner -->
          <div
            v-if="submission.grade && submission.grade.ai_error"
            class="alert alert-danger d-flex justify-content-between align-items-center mb-3"
            role="alert"
          >
            <div>
              <strong><i class="material-icons icon-16pt mr-1">error</i> AI grading failed</strong>
              <!-- <div class="small mt-1">{{ submission.grade.ai_error }}</div> -->
            </div>
            <b-btn
              v-if="canRetryAi"
              size="sm"
              variant="danger"
              :disabled="isRetryingAi"
              @click="retryAi"
            >
              <b-spinner v-if="isRetryingAi" small class="mr-1"></b-spinner>
              <i v-else class="material-icons icon-16pt mr-1">refresh</i>
              Retry AI grading
            </b-btn>
          </div>

          <!-- Assignment Info Card -->
          <div class="card mb-3">
            <div class="card-header bg-light">
              <div class="assignment-header-main">
                <h5 class="mb-0 assignment-title">{{ submission.assignment_details.title }}</h5>
              </div>
              <div class="assignment-status-row">
                <b-badge :variant="getReviewStatusVariant(submission.review_status)" pill class="assignment-status-badge">
                  <i class="material-icons icon-16pt mr-1">{{ getReviewStatusIcon(submission.review_status) }}</i>
                  {{ getReviewStatusText(submission.review_status) }}
                </b-badge>
                <span v-if="submission.grade && submission.grade.review_status" class="assignment-meta-chip">
                  Grading status: {{ formatStatusLabel(submission.grade.review_status) }}
                </span>
                <span v-if="gradingMethod" class="assignment-meta-chip">
                  Method: {{ getGradingMethodLabel(gradingMethod) }}
                </span>
              </div>
              <p class="text-muted mb-0 mt-2" v-if="submission.assignment_details.description">
                {{ submission.assignment_details.description }}
              </p>
            </div>
            <div class="card-body py-3">
              <div class="row">
                <div class="col-md-3">
                  <div class="info-item">
                    <i class="material-icons text-primary mr-2">person</i>
                    <div>
                      <small class="text-muted d-block">Student</small>
                      <strong>{{ submission.student_name }}</strong>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="info-item">
                    <i class="material-icons text-info mr-2">event</i>
                    <div>
                      <small class="text-muted d-block">Reviewed On</small>
                      <strong>{{ submission.grade ? formatDate(submission.grade.graded_at) : 'Not graded yet' }}</strong>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="info-item">
                    <i class="material-icons text-warning mr-2">assignment</i>
                    <div>
                      <small class="text-muted d-block">Tasks Completed</small>
                      <strong>{{ submission.completed_tasks }} / {{ submission.total_tasks }}</strong>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="info-item">
                    <i class="material-icons text-success mr-2">grade</i>
                    <div>
                      <small class="text-muted d-block">Weightage</small>
                      <strong>{{ submission.assignment_details.weightage }}%</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- View All Attempts Button -->
          <div class="mb-3 text-right">
            <b-btn
              variant="primary"
              @click="viewAllAttempts"
              :style="{ backgroundColor: primaryColor, borderColor: primaryColor }"
            >
              <i class="material-icons icon-16pt mr-1">visibility</i>
              View All Attempts
            </b-btn>
          </div>

          <!-- Already Graded Alert -->
          <div v-if="submission.review_status === 'graded' && submission.grade" class="alert alert-success mb-3">
            <div class="d-flex align-items-center">
              <i class="material-icons icon-20pt mr-2">check_circle</i>
              <div>
                <strong>This submission has been reviewed and graded.</strong>
                <small class="d-block mt-1">
                  Graded on {{ formatDate(submission.grade.graded_at) }} by {{ submission.grade.graded_by_name }}
                </small>
              </div>
            </div>
          </div>

          <!-- Grading Form -->
          <div class="card mb-3">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="material-icons icon-16pt mr-2">rate_review</i>
                Grade Assignment
              </h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <!-- Score Input -->
                  <b-form-group label="Score Obtained" label-for="score-input" label-class="form-label">
                    <div class="score-input-wrapper">
                      <b-form-input
                        id="score-input"
                        v-model.number="scoreObtained"
                        type="number"
                        min="0"
                        :max="totalMarks"
                        step="0.5"
                        placeholder="Enter score"
                        :state="scoreValidation"
                      />
                      <span class="score-divider">/</span>
                      <span class="total-marks">{{ totalMarks }}</span>
                    </div>
                    <b-form-invalid-feedback :state="scoreValidation">
                      Score must be between 0 and {{ totalMarks }}
                    </b-form-invalid-feedback>
                    <small class="form-text text-muted">
                      Enter the total marks obtained by the student out of {{ totalMarks }} marks.
                    </small>
                  </b-form-group>
                </div>
                <div class="col-md-6">
                  <!-- Feedback -->
                  <b-form-group label="Overall Feedback" label-for="feedback-input" label-class="form-label">
                    <b-form-textarea
                      id="feedback-input"
                      v-model="overallFeedback"
                      rows="4"
                      placeholder="Provide feedback for the student..."
                    />
                    <small class="form-text text-muted">
                      Share constructive feedback to help the student improve.
                    </small>
                  </b-form-group>
                </div>
              </div>

              <!-- Submit Button -->
              <div class="text-right mt-2">
                <b-btn
                  variant="success"
                  @click="submitGrade"
                  :disabled="isSubmitting || !isFormValid"
                  class="px-4"
                >
                  <b-spinner v-if="isSubmitting" small class="mr-2"></b-spinner>
                  <i v-else class="material-icons icon-16pt mr-2">check_circle</i>
                  {{ submission.review_status === 'graded' ? 'Update Grade' : 'Submit Grade' }}
                </b-btn>
              </div>
            </div>
          </div>

          <!-- AI Task Breakdown -->
          <div v-if="hasAiBreakdown" class="card mb-3">
            <div class="card-header">
              <div class="d-flex justify-content-between align-items-center flex-wrap">
                <h5 class="mb-0">
                  <i class="material-icons icon-16pt mr-2">assessment</i>
                  AI grading breakdown
                </h5>
                <small v-if="aiModel || aiTotalTokens" class="text-muted ai-breakdown-meta">
                  <span v-if="aiModel"><strong>Model:</strong> {{ aiModel }}</span>
                  <span v-if="aiTotalTokens" class="ml-3"><strong>Tokens:</strong> {{ aiTotalTokens }}</span>
                </small>
              </div>
            </div>
            <div class="card-body">
              <div v-for="task in aiBreakdownTasks" :key="task.id" class="mb-3 ai-breakdown-task">
                <button
                  type="button"
                  class="ai-breakdown-task-toggle"
                  @click="toggleAiTask(task.id)"
                >
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="mb-0 ai-task-title">{{ task.title }}</h6>
                    <div class="text-muted small d-flex align-items-center ai-task-score">
                      <strong>{{ task.score }}</strong> / {{ task.max }}
                      <i class="material-icons icon-18pt ml-1">{{ isAiTaskOpen(task.id) ? 'expand_less' : 'expand_more' }}</i>
                    </div>
                  </div>
                </button>
                <b-collapse :visible="isAiTaskOpen(task.id)" class="ai-breakdown-task-content">
                  <div
                    v-if="task.verificationBadge"
                    class="verification-pill mb-2"
                    :class="task.verificationBadge.verified ? 'verification-pill--ok' : 'verification-pill--fail'"
                  >
                    <i class="material-icons icon-14pt mr-1">
                      {{ task.verificationBadge.verified ? 'check_circle' : 'cancel' }}
                    </i>
                    {{ task.verificationBadge.verified ? 'Verified' : 'Not verified' }}:
                    {{ task.verificationBadge.label }}
                  </div>
                  <!-- <div v-if="task.error" :class="getTaskErrorCalloutClass(task.error)">
                    <i class="material-icons icon-12pt">warning</i>
                    {{ getTaskErrorMessage(task.error) }}
                  </div> -->
                  <blockquote v-if="task.feedback" class="ai-feedback mb-2">
                    {{ task.feedback }}
                  </blockquote>
                  <b-table
                    v-if="!task.isCheckbox && task.criteria_scores && task.criteria_scores.length"
                    :fields="criteriaFields"
                    :items="task.criteria_scores"
                    small
                    borderless
                    class="mb-0 ai-criteria-table"
                  />
                </b-collapse>
              </div>
            </div>
          </div>

          <!-- Task Submissions (View Only) -->
          <div v-if="submission.tasks_with_submissions && submission.tasks_with_submissions.length" class="card">
            <div class="card-header">
              <h5 class="mb-0">
                <i class="material-icons icon-16pt mr-2">list_alt</i>
                Task Submissions
              </h5>
            </div>
            <div class="card-body">
              <div v-for="(task, index) in submission.tasks_with_submissions" :key="task.id" class="task-item">
                <div class="d-flex align-items-start">
                  <div class="task-number">{{ index + 1 }}</div>
                  <div class="flex-grow-1">
                    <h6 class="mb-2">{{ task.title }}</h6>
                    <div class="text-muted mb-2" v-if="task.description" v-html="task.description"></div>
                    <div class="submission-response mb-2" v-if="getTaskEvidenceNote(task)">
                      <small class="response-label d-block font-weight-bold mb-1">
                        {{ task.completion_mode === 'case_study' ? 'Case Study Response' : 'Student Response' }}
                      </small>
                      <div class="response-text mb-0" v-html="getTaskEvidenceNote(task)"></div>
                    </div>
                    <div v-if="task.submission">
                      <b-badge variant="success" class="mr-2">
                        <i class="material-icons icon-12pt">check</i> Completed
                      </b-badge>
                    </div>
                    <b-badge v-else variant="secondary">
                      <i class="material-icons icon-12pt">close</i> Not Completed
                    </b-badge>
                  </div>
                </div>
                <hr v-if="index < submission.tasks_with_submissions.length - 1" class="my-3">
              </div>
            </div>
          </div>
        </div>

        <!-- Error State -->
        <div v-else class="text-center py-5">
          <p class="text-danger">Failed to load submission details.</p>
          <b-btn variant="primary" @click="fetchSubmissionDetails">Retry</b-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
import { get } from 'lodash';
import Page from '@/components/Page.vue';
import PageHeader from '@/components/Ui/PageHeader.vue';
import { REVIEW_STATUS_META, GRADING_METHOD_META } from '@/common/aiGradingTemplates';

export default {
  components: {
    PageHeader,
  },
  extends: Page,

  data() {
    return {
      title: 'Grade Submission',
      isLoading: true,
      isSubmitting: false,
      isRetryingAi: false,
      submission: null,
      scoreObtained: 0,
      overallFeedback: '',
      aiExpandedTasks: {},
      criteriaFields: [
        { key: 'criterion', label: 'Criterion' },
        { key: 'score', label: 'Score', class: 'text-center', formatter: (v, k, item) => `${v} / ${item.max}` },
        { key: 'comment', label: 'Comment' },
      ],
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    gradingMethod() {
      return get(this.submission, 'grade.grading_method') || get(this.submission, 'grading_method') || '';
    },
    aiModel() {
      return get(this.submission, 'grade.ai_model') || get(this.submission, 'ai_model') || '';
    },
    aiTotalTokens() {
      return get(this.submission, 'grade.ai_token_usage.total_tokens')
        || get(this.submission, 'ai_token_usage.total_tokens') || 0;
    },
    aiBreakdownRaw() {
      return get(this.submission, 'grade.ai_task_breakdown')
        || get(this.submission, 'ai_task_breakdown') || {};
    },
    hasAiBreakdown() {
      return this.aiBreakdownRaw && Object.keys(this.aiBreakdownRaw).length > 0;
    },
    aiBreakdownTasks() {
      const tasks = (this.submission && this.submission.tasks_with_submissions) || [];
      const titleById = {};
      const completionModeById = {};
      tasks.forEach((t) => {
        titleById[t.id] = t.title;
        completionModeById[t.id] = t.completion_mode;
      });
      return Object.entries(this.aiBreakdownRaw).map(([taskId, data]) => {
        const criteriaScores = data.criteria_scores || [];
        const verificationCriterion = Array.isArray(criteriaScores)
          ? criteriaScores.find(c => Object.prototype.hasOwnProperty.call(c || {}, 'verified'))
          : null;
        const completionMode = completionModeById[taskId] || '';

        return {
          id: taskId,
          title: titleById[taskId] || `Task ${String(taskId).slice(0, 8)}`,
          completion_mode: completionMode,
          isCheckbox: completionMode === 'checkbox',
          score: data.score != null ? data.score : 0,
          max: data.max != null ? data.max : 0,
          feedback: data.feedback || '',
          criteria_scores: criteriaScores,
          error: data.error || null,
          verificationBadge: verificationCriterion
            ? {
                verified: !!verificationCriterion.verified,
                label: this.getCheckboxActionLabel(verificationCriterion.abbr),
              }
            : null,
        };
      });
    },
    canRetryAi() {
      const allowed = ['ai_grading_failed', 'in_review', 'pending'];
      return allowed.includes(this.submission && this.submission.review_status);
    },
    primaryColor() {
      return get(this.getLoggedInUser, 'linked_entity.primary_color', '#3b82f6');
    },
    totalMarks() {
      // Get total marks from grade if exists, otherwise from assignment details
      return this.submission?.grade?.total_marks || this.submission?.assignment_details?.total_marks || 0;
    },
    scoreValidation() {
      if (this.scoreObtained === '' || this.scoreObtained === null) return null;
      const score = parseFloat(this.scoreObtained);
      return score >= 0 && score <= this.totalMarks;
    },
    isFormValid() {
      if (this.scoreObtained === '' || this.scoreObtained === null) return false;
      const score = parseFloat(this.scoreObtained);
      return score >= 0 && score <= this.totalMarks;
    },
  },

  methods: {
    ...mapActions('gradedSubmissions', ['getGradedSubmissionDetails', 'submitGrade', 'retryAiGrade']),

    getGradingMethodLabel(method) {
      const meta = GRADING_METHOD_META[method];
      return meta ? meta.label : method;
    },
    getGradingMethodVariant(method) {
      const meta = GRADING_METHOD_META[method];
      return meta ? meta.variant : 'secondary';
    },

    async retryAi() {
      if (this.isRetryingAi) return;
      this.isRetryingAi = true;
      try {
        await this.retryAiGrade(this.$route.params.id);
        this.makeToast({ variant: 'success', msg: 'AI grading re-queued.' });
        await this.fetchSubmissionDetails();
      } catch (e) {
        const msg = (e.response && (e.response.data.detail || e.response.data.message
          || e.response.data.error)) || 'Failed to retry AI grading';
        this.makeToast({ variant: 'danger', msg });
      } finally {
        this.isRetryingAi = false;
      }
    },

    async fetchSubmissionDetails() {
      this.isLoading = true;
      try {
        const response = await this.getGradedSubmissionDetails(this.$route.params.id);
        this.submission = response.data;
        this.initializeAiTaskState();

        // Pre-fill if already graded
        if (this.submission.grade) {
          this.scoreObtained = this.submission.grade.score_obtained || 0;
          this.overallFeedback = this.submission.grade.overall_feedback || '';
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: 'Failed to fetch submission details' });
        this.submission = null;
      } finally {
        this.isLoading = false;
      }
    },

    async submitGrade() {
      if (!this.isFormValid) {
        this.makeToast({ variant: 'warning', msg: 'Please enter a valid score' });
        return;
      }

      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Are you sure you want to submit this grade? The student will be notified.',
        {
          title: 'Confirm Grade Submission',
          okVariant: 'success',
          okTitle: 'Submit Grade',
          cancelTitle: 'Cancel',
          centered: true,
        }
      );

      if (!confirmed) return;

      this.isSubmitting = true;
      try {
        const gradeData = {
          score_obtained: parseFloat(this.scoreObtained),
          overall_feedback: this.overallFeedback || '',
        };

        // Call the API directly using axios
        const response = await axios.post(
          `api/lms/graded-submissions/${this.$route.params.id}/grade/`,
          gradeData
        );

        this.makeToast({ variant: 'success', msg: response.data.message || 'Grade submitted successfully' });
        
        // Redirect back after a short delay
        setTimeout(() => {
          const tab = this.$route.query.tab;
          if (tab !== undefined) {
            this.$router.push({
              name: 'list-graded-submissions',
              query: { tab },
            });
          } else {
            this.$router.go(-1);
          }
        }, 1500);
      } catch (error) {
        const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to submit grade';
        this.makeToast({ variant: 'danger', msg: errorMsg });
      } finally {
        this.isSubmitting = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    formatStatusLabel(status) {
      if (!status) return '-';
      return String(status)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
    },

    getReviewStatusText(status) {
      const meta = REVIEW_STATUS_META[status];
      return meta ? meta.label : status;
    },

    getReviewStatusVariant(status) {
      const meta = REVIEW_STATUS_META[status];
      return meta ? meta.variant : 'secondary';
    },

    getReviewStatusIcon(status) {
      const meta = REVIEW_STATUS_META[status];
      return meta ? meta.icon : 'help';
    },

    getTaskEvidenceNote(task) {
      return get(task, 'submission.evidence.note') || get(task, 'evidence.note') || get(task, 'submission.note') || '';
    },

    getCheckboxActionLabel(abbr) {
      if (!abbr) return 'Verified action';
      return String(abbr)
        .split('_')
        .map(part => (part ? part.charAt(0).toUpperCase() + part.slice(1) : part))
        .join(' ');
    },
    getTaskErrorCalloutClass(errorCode) {
      if (!errorCode) return 'alert alert-warning py-2 mb-2';
      const manualReviewCodes = ['action_not_verified', 'unknown_action', 'vision_render_failed', 'vision_provider_error'];
      if (manualReviewCodes.includes(errorCode)) return 'alert alert-danger py-2 mb-2';
      return 'alert alert-warning py-2 mb-2';
    },
    getTaskErrorMessage(errorCode) {
      if (errorCode === 'vision_render_failed') {
        return "Couldn't render this scanned PDF. Ask the student to re-upload as a clearer scan or as separate JPG/PNG photos, or grade manually.";
      }
      if (errorCode === 'vision_provider_error') {
        return "The AI provider couldn't read this image submission. Grade manually or switch the active provider in Superadmin → AI Provider Config.";
      }
      return 'Per-task AI: Task not found grade this task manually.';
    },

    initializeAiTaskState() {
      const nextState = {};
      Object.keys(this.aiBreakdownRaw || {}).forEach((taskId) => {
        nextState[taskId] = this.aiExpandedTasks[taskId] || false;
      });
      this.aiExpandedTasks = nextState;
    },

    isAiTaskOpen(taskId) {
      return !!this.aiExpandedTasks[String(taskId)];
    },

    toggleAiTask(taskId) {
      const key = String(taskId);
      this.$set(this.aiExpandedTasks, key, !this.aiExpandedTasks[key]);
    },

    viewAllAttempts() {
      if (!this.submission) return;
      
      // Get IDs from submission - the API returns these fields
      const studentId = this.submission.student_id || this.submission.student;
      const assignmentId = this.submission.assignment_id || this.submission.assignment || get(this.submission, 'assignment_details.id');
      const tab = this.$route.query.tab;
      
      if (!assignmentId || !studentId) {
        this.makeToast({ variant: 'warning', msg: 'Missing assignment or student details' });
        return;
      }

      this.$router.push({
        name: 'assignment-attempts',
        params: {
          enrollmentId: 'graded-submissions',
          assignmentId: assignmentId,
        },
        query: {
          student_id: studentId,
          ...(tab !== undefined && { tab }),
        },
      });
    },
  },

  created() {
    this.fetchSubmissionDetails();
  },
};
</script>

<style scoped>
.ai-feedback {
  border-left: 3px solid #6366f1;
  background: #eef2ff;
  padding: 0.5rem 0.75rem;
  color: #1e1b4b;
  border-radius: 0 4px 4px 0;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}
.ai-criteria-table ::v-deep th {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
}
.clickable-item {
  cursor: pointer;
}

.assignment-header-main {
  margin-bottom: 0.5rem;
}

.assignment-title {
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.35;
}

.assignment-status-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.assignment-status-badge {
  padding: 0.45rem 0.75rem;
}

.assignment-meta-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  border: 1px solid #dbe4ef;
  background: #f8fafc;
  color: #475569;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1;
}

.ai-breakdown-meta {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.ai-breakdown-task {
 
  padding-bottom: 0.8rem;
}

.ai-breakdown-task:last-child {
  border-bottom: 0;
  padding-bottom: 0;
  margin-bottom: 0;
}

.ai-breakdown-task-toggle {
  width: 100%;
  text-align: left;
  display: block;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  border-radius: 6px;
  padding: 0.65rem 0.85rem;
  transition: background-color 0.2s ease, border-color 0.2s ease;
  color: inherit;
  text-decoration: none;
}

.ai-breakdown-task-toggle:hover,
.ai-breakdown-task-toggle:focus {
  background: #f1f5f9;
  border-color: #cbd5e1;
  outline: none;
}

.ai-task-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #334155;
  text-transform: none;
}

.ai-task-score {
  flex-shrink: 0;
}

.ai-breakdown-task-content {
  margin-top: 0.6rem;
}

.icon-18pt {
  font-size: 18px;
  vertical-align: middle;
}

.icon-14pt {
  font-size: 14px;
  vertical-align: middle;
}

.verification-pill {
  display: inline-flex;
  align-items: center;
  font-size: 0.78rem;
  font-weight: 600;
  border-radius: 999px;
  padding: 0.28rem 0.7rem;
}

.verification-pill--ok {
  background: #ecfdf3;
  color: #166534;
  border: 1px solid #86efac;
}

.verification-pill--fail {
  background: #fef2f2;
  color: #991b1b;
  border: 1px solid #fecaca;
}

.info-item {
  display: flex;
  align-items: center;
}

.info-item i {
  font-size: 20px;
}

.score-input-wrapper {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.score-input-wrapper input {
  font-size: 1.25rem;
  font-weight: 600;
  text-align: center;
  max-width: 120px;
}

.score-divider {
  font-size: 1.25rem;
  font-weight: 600;
  color: #6c757d;
  margin: 0 0.5rem;
}

.total-marks {
  font-size: 1.25rem;
  font-weight: 600;
  color: #6c757d;
}


.task-item {
  padding: 0.5rem 0;
}

.task-number {
  width: 32px;
  height: 32px;
  background: #007bff;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  margin-right: 1rem;
  flex-shrink: 0;
}

.submission-response {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.875rem 1rem;
}

.response-label {
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  font-size: 0.75rem;
}

.response-text {
  color: #1f2937;
  line-height: 1.55;
  overflow-x: auto;
  word-break: break-word;
}

.response-text :deep(*) {
  max-width: 100%;
}

.response-text :deep(img),
.response-text :deep(video),
.response-text :deep(iframe),
.response-text :deep(table) {
  max-width: 100%;
}

.icon-12pt {
  font-size: 12pt;
  vertical-align: middle;
}

@media (max-width: 767px) {
  .assignment-title {
    font-size: 1.05rem;
  }

  .ai-breakdown-meta {
    display: inline-block;
    width: 100%;
    white-space: normal;
    text-align: right;
  }
}
</style>
